import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import DOMPurify from 'dompurify';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CloseIcon from '@material-ui/icons/Close';

const INFO_COPY = {
  //**ダッシュボード
  // 左サイドバー: ダッシュボード
  'sidebar-dashboard': {
    text: '指定した期間のNPSや顧客接点満足度、ランキングなどを確認できます。'
  },
  // 左サイドバー: フィードバック一覧
  'sidebar-feedbacks': {
    text: 'お客様から届いたアンケートの回答結果（フィードバック）を確認できます。そのフィードバックを並び替えたり、フィードバック単位でコメントやいいねをして認知称賛もできます。'
  },
  // 左サイドバー: NPS分析
  'sidebar-analysis': {
    text: 'NPSについて指定期間の値や、推奨度の分布、推移、相関分析の結果を確認できます。'
  },
  //認知賞賛
  'like-recognition': {
    text: 'お客様からの声に対して、社内のメンバー（同僚・上司）から寄せられたリアクションの数です。'
  },
  //フィードバックいいね
  'like-feedback': {
    text: 'お客様の声（フィードバック）に対して、同僚や上司が「いいね」を送った数です。'
  },
  //コメントいいね
  'like-comment': {
    text: '同僚や上司からのコメントに対して、さらに「いいね」がついた数です。'
  },
  //コメント
  'comment-count': {
    text: 'お客様の声に対して、同僚や上司がコメントを送った件数です。'
  },
  //顧客接点満足度
  'customer-touchpoint-satisfaction': {
    text: 'アンケート接客やサービスごとの満足度を、割合や平均値で表します。どの体験に満足されているかがひと目でわかります。'
  },
  //顧客接点満足度内　表TOP率列
  'top-two-rate-delta': {
    text: '選択した期間と、その直前の同じ日数の期間を比較した顧客接点満足度の増減を、＋／－で表示しています。'
  },
  //**NPS分析
  //カレンダー
  'date-range': {
    text: '期間指定：任意の期間を指定できます。指定した期間は、画面上の各項目の結果に反映されます。'
  },
  //絞込条件
  'filter-add-condition': {
    text: 'アンケート設問の選択肢でデータを絞りこむ機能です。例えば、性別設問を選択した上で女性で絞込条件を設定すると、女性と回答した方のみの結果が表示されます。'
  },
  //NPS
  'nps-score': {
    text: '「どれくらいのお客様が、人にすすめたいと思っているか」を表す数値です。数値は-100〜+100の間で表示され、高いほど、「すすめたいと思っている人が多い」ということです。'
  },
  //回答数
  'feedback-count': {
    text: '指定した期間にアンケートにご回答いただけた数です。'
  },
  //NPSカテゴリ
  'nps-category': {
    text:
      'お客様の答えた推奨度の点数(０～10点)に応じて3つのグループに分けています。\n・🟩 推奨者（9〜10点をつけた人）：\n　「とても良かった！人にもすすめたい」と思っているお客様です。\n・🟨 中立者（7〜8点）：\n　「良かったけど、すすめるかは分からない」と思っているお客様です。\n・🟥 批判者（0〜6点）：\n　「普通、すすめるほどではない、すすめたくない」と思っているお客様です。'
  },
  //期間合計(タブ)
  'analysis-periods': {
    text: '指定した期間の推奨度の点数を、回答数・割合・グラフで確認できます。'
  },
  //期間合計(タブ): 回答数ボタン
  'timeseries-answers': {
    text:
      '指定した期間内でどれだけのお客様がアンケートに答えてくれたかの推移を表しています。どんな時に回答数が増減するかを確認し、増減の理由を考えるきっかけになります。'
  },
  //時系列推移(タブ)
  'analysis-timeseries': {
    text:
      'NPSなど各指標の変化を、時間の流れで見る画面です。\nたとえば、「先月より今月のNPSは上がった？下がった？」といった変化を、グラフで確認できます。'
  },
  //時系列(タブ): NPS値ボタン
  'timeseries-nps-score': {
    text:
      '指定した期間内で、NPSがどのように推移しているかを表しています。どんな時にNPSが増減するかを確認し、増減の理由を考えるきっかけになります。'
  },
  //時系列(タブ): NPSカテゴリシェアボタン
  'timeseries-category-share': {
    text:
      '指定した期間内で、「推奨者」「中立者」「批判者」の割合がどのように推移しているかを表しています。どんな時に「推奨者」が増えたか、「批判者」が増えたかを確認し、変化の理由を考えるきっかけになります。'
  },
  //相関分析(タブ) 非アクティブ
  'analysis-correlations': {
    text:
      'この画面では「どの体験が、お客様の“また来たい・人にすすめたい気持ち”につながっているのか？」がわかります。つまり、どこを改善・強化すればNPSが上がるかを見つけるヒントになります。'
  },
  //相関分析(タブ) 図表
  'correlation-priority': {
    text:
      '優先課題（左上）\n満足している人が少なく、しかもNPSとの関係が強い＝「改善すればNPSに大きな影響がある」重要なポイントです。'
  },
  //相関分析(タブ) 図表
  'correlation-watch': {
    text:
      '注意観察（左下）\n満足している人が少なく、NPSとの関係も弱い＝今すぐ改善は必要ないが、様子を見ておきたい項目です。'
  },
  //相関分析(タブ) 図表
  'correlation-basic': {
    text:
      '基本維持（右下）\n満足している人は多いが、NPSとの関係は弱い＝基本は満足してもらっているので、大きな課題ではありません。'
  },
  //相関分析(タブ) 図表
  'correlation-maintain': {
    text:
      '維持強化（右上）\n満足している人も多く、NPSとも強く関係＝「今の良さをしっかり維持・強化する」価値のある項目です。'
  },
  //相関分析(タブ) 図表 X軸ラベル 満足度(TOP2)
  'correlation-satisfaction': {
    text:
      '満足度：\n右に行くほど良いと評価する人が多い\n左に行くほど良いと評価する人が少ない'
  },
  //相関分析(タブ) 図表 Y軸ラベル NPS相関係数
  'correlation-nps-coefficient': {
    text:
      'NPSとの関係の強さ（＝相関）を表しており、上に行くほど関係性が強いことを示しています。'
  },
  //相関分析(タブ) 図表　x軸の平均値
  'correlation-satisfaction-average': {
    text: 'このライン（数値）は、選んだ店舗や組織の「満足度の平均ライン」です。'
  },
  //相関分析(タブ) 図表　y軸の平均値
  'correlation-nps-coefficient-average': {
    text: 'このライン（数値）は、選んだ店舗や組織での「NPSとの関係の強さ」の平均ラインです。'
  },
  //**アンケート集計
  //アンケート集計 ページ説明
  'aggregation-overview': {
    text:
      'アンケートの中から、見たい質問（設問）を選んで、その集計結果を見ることができます。例えば、「年代別の回答の違いを見たい」といった場合に使います。'
  },
  //設問集計(タブ) 設問選択ボックス
  'aggregation-question-select': {
    text:
      '設問選択ボックス（中央のプルダウン）：アンケートでお客様に聞いたすべての質問が一覧で表示されます。例えば、「お客様の性別は？」や「どのような歓迎感を感じましたか？」など、質問文そのままで表示されます。見たい質問を選ぶと、その設問に関する回答件数・割合などが表示されます。'
  },
  //クロス集計(タブ) 概要
  'aggregation-cross-overview': {
    text:
      'この画面では2つの設問を掛け合わせて（クロスして）、お客様の傾向の違いを見ることができます。たとえば「年代 × NPS」や「性別 × 満足度」のように設定することで、「どの年代のお客様が特に満足しているのか」などがひと目でわかります。'
  },
  //クロス集計(タブ) 縦設問
  'cross-x-question': {
    text:
      '表の「横方向（列）」に並ぶ項目です。横軸（列）は、比較したいもう1つの項目（例：満足度、回答結果）を選択します。'
  },
  //クロス集計(タブ) 横設問
  'cross-y-question': {
    text:
      '表の「縦方向（行）」に並ぶ項目です。縦軸（行）は、主な分析対象（例：性別、年代、NPSカテゴリ）などを選択します。'
  },
};

const useStyles = makeStyles(() => ({
  root: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: 6,
    cursor: 'pointer',
  },
  iconButton: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    color: '#1f71c2',
    background: 'transparent',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    padding: 0,
  },
  bubbleBase: {
    position: 'fixed',
    background: '#0D3480',
    color: '#fff',
    padding: '12px 14px',
    borderRadius: 10,
    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
    fontSize: 12,
    lineHeight: 1.6,
    minWidth: 220,
    maxWidth: 360,
    zIndex: 2200,
    pointerEvents: 'none',
    whiteSpace: 'pre-line',
  },
  bubbleTop: {
    transform: 'translate(-50%, calc(-100% - 8px))',
  },
  bubbleBottom: {
    transform: 'translate(-50%, 8px)',
  },
  arrowBase: {
    position: 'absolute',
    left: '50%',
    width: 0,
    height: 0,
    transform: 'translateX(-50%)',
  },
  arrowTop: {
    bottom: -8,
    borderLeft: '8px solid transparent',
    borderRight: '8px solid transparent',
    borderTop: '8px solid #0D3480',
    content: '""',
  },
  arrowBottom: {
    top: -8,
    borderLeft: '8px solid transparent',
    borderRight: '8px solid transparent',
    borderBottom: '8px solid #0D3480',
    content: '""',
  },
  tight: {
    marginLeft: 4,
  },
  smallIcon: {
    width: 20,
    height: 20,
  },
}));

const InfoHint = ({
  infoId,
  className,
  dense = false,
  stopPropagation = false,
  placement = 'bottom',
  // インスタンス単位で配色を上書きしたい場合に指定
  iconColor,
  bubbleColor,
  bubbleTextColor,
}) => {
  const classes = useStyles();
  const [isPcPointer, setIsPcPointer] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, anchorX: 0 });
  const [arrowLeft, setArrowLeft] = useState(null);
  const anchorRef = useRef(null);
  const bubbleRef = useRef(null);
  const copy = useMemo(() => INFO_COPY[infoId] || {}, [infoId]);
  const open = pinned || hovering;
  const isTop = placement === 'top';
  const hasContent = Boolean(copy.text || copy.html);

  // PC(マウス)では hover で開かず、クリックで開く挙動にする
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    const apply = () => setIsPcPointer(Boolean(mediaQuery.matches));
    apply();
    const handler = () => apply();
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
    mediaQuery.addListener(handler);
    return () => mediaQuery.removeListener(handler);
  }, []);

  const handleIconClick = event => {
    if (stopPropagation) {
      event.stopPropagation();
    }
    if (pinned) {
      setPinned(false);
      setHovering(false);
      return;
    }
    setPinned(true);
  };

  const handleMouseEnter = () => {
    if (isPcPointer) return;
    setHovering(true);
  };
  const handleMouseLeave = () => {
    if (isPcPointer) return;
    setHovering(false);
  };

  // 外側タップで閉じる: モバイルで吹き出し外をタップしたときに確実に閉じる
  useEffect(() => {
    if (!open) return;
    const handleDocumentClick = event => {
      const target = event.target;
      if (!anchorRef.current) return;
      // クリックがアイコン自身もしくは吹き出し(Portal配下)なら無視
      const isInsideAnchor = anchorRef.current.contains(target);
      // optional chaining 非依存: closest が無い環境でも安全に参照
      const isInsideBubble = target && typeof target.closest === 'function'
        ? target.closest('[data-infohint-bubble="true"]')
        : null;
      if (isInsideAnchor || isInsideBubble) return;
      setPinned(false);
      setHovering(false);
    };
    document.addEventListener('click', handleDocumentClick, true);
    document.addEventListener('touchstart', handleDocumentClick, true);
    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
      document.removeEventListener('touchstart', handleDocumentClick, true);
    };
  }, [open]);

  useEffect(() => {
    const updateCoords = () => {
      if (!open || !anchorRef.current) return;
      const rect = anchorRef.current.getBoundingClientRect();
      const gap = 0;
      // InfoHint の吹き出し（max-width: 360px）が、画面の左右端をはみ出さないように位置をクランプ（制限）
      const centerX = rect.left + rect.width / 2;
      const margin = 12;           
      const halfWidth = 180;      
      const minLeft = margin + halfWidth;
      const maxLeft = Math.max(minLeft, window.innerWidth - margin - halfWidth);
      const clampedLeft = Math.min(Math.max(centerX, minLeft), maxLeft); 
      //計算後の表示位置を state に反映
      setCoords({
        left: clampedLeft,
        top: isTop ? rect.top - gap : rect.bottom + gap,
        anchorX: centerX,
      });
    };

    updateCoords();
    if (!open) return undefined;

    window.addEventListener('scroll', updateCoords, true);
    window.addEventListener('resize', updateCoords);
    return () => {
      window.removeEventListener('scroll', updateCoords, true);
      window.removeEventListener('resize', updateCoords);
    };
  }, [open, isTop]);

  // 吹き出しが画面端に寄っても、三角形がiマークの方向から出るように補正する
  useEffect(() => {
    if (!open || !hasContent) return;
    const updateArrow = () => {
      if (!bubbleRef.current) return;
      const rect = bubbleRef.current.getBoundingClientRect();
      const rawLeft = coords.anchorX - rect.left;
      const padding = 16; // 吹き出し端に寄りすぎないように制限
      const clampedLeft = Math.min(Math.max(rawLeft, padding), rect.width - padding);
      setArrowLeft(clampedLeft);
    };
    const rafId = requestAnimationFrame(updateArrow);
    return () => cancelAnimationFrame(rafId);
  }, [open, hasContent, coords.anchorX, coords.left, coords.top, copy.html, copy.text]);

  if (!hasContent) return null;

  const sanitizedHtml = copy.html ? DOMPurify.sanitize(copy.html) : '';

  const bubbleContent = copy.html ? (
    <span dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
  ) : copy.text;

  const bubbleStyle = {
    left: coords.left,
    top: coords.top,
    ...(bubbleColor ? { background: bubbleColor } : {}),
    ...(bubbleTextColor ? { color: bubbleTextColor } : {}),
  };

  const arrowStyle = {
    ...(arrowLeft != null ? { left: arrowLeft } : {}),
    ...(bubbleColor ? (isTop ? { borderTopColor: bubbleColor } : { borderBottomColor: bubbleColor }) : {}),
  };

  const bubble = open ? (
    <span
      ref={bubbleRef}
      data-infohint-bubble="true"
      className={clsx(classes.bubbleBase, isTop ? classes.bubbleTop : classes.bubbleBottom)}
      style={bubbleStyle}
    >
      <span
        className={clsx(classes.arrowBase, isTop ? classes.arrowTop : classes.arrowBottom)}
        style={Object.keys(arrowStyle).length ? arrowStyle : undefined}
      />
      {bubbleContent}
    </span>
  ) : null;

  return (
    <span
      ref={anchorRef}
      className={clsx(classes.root, dense && classes.tight, className)}
      onClick={handleIconClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span
        className={clsx(classes.iconButton, dense && classes.smallIcon)}
        style={iconColor ? { color: iconColor } : undefined}
      >
        {open ? <CloseIcon fontSize="small" /> : <InfoOutlinedIcon fontSize="small" />}
      </span>
      {open && ReactDOM.createPortal(bubble, document.body)}
    </span>
  );
};

InfoHint.propTypes = {
  infoId: PropTypes.string.isRequired,
  className: PropTypes.string,
  dense: PropTypes.bool,
  stopPropagation: PropTypes.bool,
  placement: PropTypes.oneOf(['top', 'bottom']),
  iconColor: PropTypes.string,
  bubbleColor: PropTypes.string,
  bubbleTextColor: PropTypes.string,
};

export default InfoHint;
